import React, { useState, useEffect } from 'react';
import { ChevronRight, ExternalLink, ArrowRight, Search, MapPin, Building, ChevronDown, Filter, X, Check, Phone, User, MessageSquare, Home, Sparkles, ShirtIcon, Backpack, Droplet } from 'lucide-react';
import P1 from '../../assets/images/p1.png';
import P2 from '../../assets/images/p2.png';
import P3 from '../../assets/images/p3.png';
import QuoteModal from './QuoteModal';

const TypingEffect = ({ words }) => {
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [displayedText, setDisplayedText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [typingSpeed, setTypingSpeed] = useState(150);

    useEffect(() => {
        const currentWord = words[currentWordIndex];

        const timer = setTimeout(() => {
            if (!isDeleting) {
                setDisplayedText(currentWord.substring(0, displayedText.length + 1));

                if (displayedText.length === currentWord.length) {
                    // Pause at complete word before deleting
                    setTypingSpeed(2000);
                    setIsDeleting(true);
                } else {
                    setTypingSpeed(150);
                }
            } else {
                setDisplayedText(currentWord.substring(0, displayedText.length - 1));

                if (displayedText.length === 0) {
                    setIsDeleting(false);
                    setCurrentWordIndex((currentWordIndex + 1) % words.length);
                    setTypingSpeed(500);
                } else {
                    setTypingSpeed(75);
                }
            }
        }, typingSpeed);

        return () => clearTimeout(timer);
    }, [currentWordIndex, displayedText, isDeleting, typingSpeed, words]);

    return (
        <span className="text-neutral-300">{displayedText}<span className="animate-pulse">|</span></span>
    );
};

const FeatureBadge = ({ text }) => {
    let Icon;
    
    switch(text) {
        case 'Dry Cleaning':
            Icon = Sparkles;
            break;
        case 'Steam Ironing':
            Icon = Droplet;
            break;
        case 'Wash & Iron':
            Icon = ShirtIcon;
            break;
        case 'Wash & Fold':
            Icon = Backpack;
            break;
        default:
            Icon = Sparkles;
    }
    
    return (
        <div className="inline-flex items-center px-3 py-1 bg-white bg-opacity-20 text-white text-sm rounded-full mr-3 mb-3">
            <Icon size={16} className="text-neutral-400 mr-2" />
            {text}
        </div>
    );
};

const StatCard = ({ value, label, delay }) => {
    const [count, setCount] = useState(0);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                }
            },
            { threshold: 0.1 }
        );

        const element = document.getElementById('hero-stats');
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, []);

    useEffect(() => {
        if (!inView) return;

        // Start the animation after the specified delay
        const animationDelay = setTimeout(() => {
            if (count < value) {
                const increment = Math.ceil(value / 20);
                const timer = setTimeout(() => {
                    setCount(prevCount => {
                        const newCount = prevCount + increment;
                        return newCount > value ? value : newCount;
                    });
                }, 100);

                return () => clearTimeout(timer);
            }
        }, delay);

        return () => clearTimeout(animationDelay);
    }, [count, value, inView, delay]);

    return (
        <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {count}<span className="text-neutral-400">+</span>
            </div>
            <p className="text-white text-opacity-80">{label}</p>
        </div>
    );
};

const Hero = () => {
    const [showQuoteModal, setShowQuoteModal] = useState(false);

    const typingWords = [
        "Cleanliness",
        "Convenience",
        "Quality",
        "Care",
        "Satisfaction"
    ];

    const features = [
        "Dry Cleaning",
        "Steam Ironing",
        "Wash & Iron",
        "Wash & Fold"
        // "Carpet Dry Cleaning",
        // "Curtain Dry Cleaning",
        // "Shoe Cleaning",
    ];

    const services = [
        'Dry Cleaning',
        'Wash & Fold',
        'Steam Ironing',
        'Wash & Iron',
        'Carpet Cleaning',
        'Curtain Cleaning',
        'Shoe Cleaning',
        'Leather Cleaning'
    ];

    const openQuoteModal = () => {
        setShowQuoteModal(true);
    };

    return (
        <>
            <section className="relative bg-gradient-to-br from-green-900 via-green-700 to-green-600 py-20 md:py-28">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-20 -right-20 w-64 h-64 bg-neutral-500 rounded-full filter blur-3xl opacity-20"></div>
                    <div className="absolute top-40 -left-20 w-96 h-96 bg-green-400 rounded-full filter blur-3xl opacity-10"></div>
                    <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-neutral-300 rounded-full filter blur-3xl opacity-10"></div>

                    <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(255, 255, 255, 0.15) 2px, transparent 0)', backgroundSize: '50px 50px' }}></div>
                </div>

                <div className="container mx-auto px-4 sm:px-6 relative z-10">
                    {/* Main Content - Centered */}
                    <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-full">
                            <div className="flex flex-wrap justify-center mb-4">
                                {features.map((feature, index) => (
                                    <FeatureBadge key={index} text={feature} />
                                ))}
                            </div>

                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white">
                                Premium Laundry <br />
                                <span className="relative inline-block">
                                    <span className="relative z-10 mr-4">Services for </span>
                                    {/* <span className="absolute bottom-2 left-0 w-full h-3 bg-neutral-300 opacity-30 z-0"></span> */}
                                </span>
                                <TypingEffect words={typingWords} />
                            </h1>

                            <p className="text-lg md:text-xl opacity-90 mb-8 leading-relaxed text-white w-full">
                                <span className="text-green-300">Urban</span><span className="text-white">Shine</span> delivers exceptional cleaning services with fast turnaround and
                                eco-friendly solutions for busy professionals and families alike.
                            </p>

                            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12">
                                <div className="flex flex-wrap items-center justify-center gap-3">
                                    <button
                                        onClick={openQuoteModal}
                                        className="px-8 py-3 border-2 border-neutral-300 text-white rounded-full font-medium hover:bg-neutral-500 hover:border-neutral-500 hover:text-white transition-colors flex items-center justify-center group"
                                    >
                                        Book Now
                                        <ExternalLink
                                            size={18}
                                            className="ml-2 transition-transform duration-300 group-hover:scale-110"
                                        />
                                    </button>
                                    <style>{`
    @keyframes borderRun {
        0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.8), inset 0 0 0 2px rgba(255,255,255,0.2); }
        50%  { box-shadow: 0 0 12px 2px rgba(255,255,255,0.5), inset 0 0 0 2px rgba(255,255,255,0.6); }
        100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.8), inset 0 0 0 2px rgba(255,255,255,0.2); }
    }
`}</style>
<div
    className="bg-amber-400 px-4 py-3 rounded-full border-2 border-white cursor-pointer"
    style={{ animation: 'borderRun 2s linear infinite' }}
>
    <span className="text-amber-900 font-bold tracking-wide text-sm">🏷️ 20% OFF on first order!</span>
</div>
                                </div>
                            </div>

                            <div className="flex items-center justify-center space-x-4">
                                <a href="#" className="flex items-center">
                                    <img
                                        src={P1}
                                        alt="Happy Customer"
                                        className="w-10 h-10 rounded-full border-2 border-white"
                                        loading="lazy"
                                    />
                                    <img
                                        src={P2}
                                        alt="Happy Customer"
                                        className="w-10 h-10 rounded-full border-2 border-white -ml-4"
                                        loading="lazy"
                                    />
                                    <img
                                        src={P3}
                                        alt="Happy Customer"
                                        className="w-10 h-10 rounded-full border-2 border-white -ml-4"
                                        loading="lazy"
                                    />
                                </a>
                                <p className="text-sm text-white opacity-90">
                                    Trusted by <span className="font-bold">2,000+</span> customers in the neighborhood
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Stats Section */}
                    <div id="hero-stats" className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 py-8 border-t border-white border-opacity-20">
                        <StatCard value={4.8} label="Google Rating" delay={500} />
                        <StatCard value={15000} label="Order Processed" delay={50} />
                        <StatCard value={285000} label="Garments Processed" delay={100} />
                        <StatCard value={2.5} label="Years of Service" delay={500} />
                    </div>
                </div>

                {/* Bottom Wave */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" fill="#f9fafb">
                        <path fillOpacity="1" d="M0,96L48,85.3C96,75,192,53,288,53.3C384,53,480,75,576,80C672,85,768,75,864,69.3C960,64,1056,64,1152,69.3C1248,75,1344,85,1392,90.7L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
                    </svg>
                </div>
            </section>

            {/* Quote Modal */}
            <QuoteModal
                isOpen={showQuoteModal}
                onClose={() => setShowQuoteModal(false)}
                services={services}
            />
        </>
    );
};

export default Hero;